import apiClient from './apiClient';
// Search API calls
export const searchService = {
  search: (keyword: string, dishLimit = 2, restaurantLimit = 2) =>
    apiClient.get('/user/search', {
      params: {
        keyword,
        dishLimit,
        restaurantLimit,
      },
    }),
};


// Type definitions
interface UserData {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface UserProfile {
  _id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email?: string;
  image?: string;
  isVerified: boolean;
  preferences?: string[];
  city?: string;
  state?: string;
  address?: string;
  geoLocation?: {
    type: string;
    coordinates: [number, number];
  };
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  email?: string;
  image?: string;
  preferences?: string[];
  city?: string;
  state?: string;
  address?: string;
  geoLocation?: {
    type: string;
    coordinates: [number, number];
  };
}

interface RestaurantParams {
  page?: number;
  limit?: number;
  category?: string;
  sortBy?: string;
}

interface OrderData {
  restaurantId: string;
  items: Array<{
    dishId: string;
    variantId: string | null;
    addons: any[];
    quantity: number;
  }>;
  paymentMethod: string;
  couponCode?: string;
  cookingInstructions?: string;
  deliveryInstructions?: string;
  deliveryAddress: {
    fullName: string;
    phone: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    pincode: string;
    geoLocation?: GeoLocation;
  };
  sessionId?: string;
}

export interface CouponData {
  _id: string;
  couponCode: string;
  description: string;
  discountType: 'percentage' | 'flat';
  discountValue: number;
  maxDiscountCap: number;
  minOrderValue: number;
  isActive: boolean;
}

export interface RazorpayOrderData {
  razorpayOrderId: string;
  amount: number;
  currency: string;
  key: string;
  mode?: string;
}

export interface CreateOrderResponse {
  orderId: string;
  pricing: {
    subtotal: number;
    taxAmount: number;
    deliveryFee: number;
    couponDiscount?: number;
    totalAmount: number;
  };
  couponApplied?: boolean;
  razorpay?: RazorpayOrderData;
}

export interface BannerData {
  _id: string;
  title: string;
  description: string;
  image: string;
  actionType: 'category' | 'restaurant' | 'cuisine' | 'external';
  actionData: {
    categoryId?: string;
    _id: string;
    restaurantDetails?: any;
    categoryDetails?: { name: string };
    cuisineTypeDetails?: any;
  };
  sortOrder: number;
}

export interface StaticPageData {
  _id: string;
  slug: string;
  title: string;
  content: string;
}

export type StaticPageSlug = 'terms-and-condition' | 'privacy-policy';

interface OrderParams {
  page?: number;
  limit?: number;
  status?: string;
}

interface GeoLocation {
  type: string;
  coordinates: number[];
}

interface DeliveryAddress {
  fullName: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  pincode: string;
  geoLocation?: GeoLocation;
}

interface RestaurantLocation {
  address: string;
  landmark: string;
  state: string;
  city: string;
  pincode: string;
  lat: number;
  lng: number;
  googleMapsLink: string;
}

interface Restaurant {
  _id: string;
  restaurantId: string;
  businessName: string;
  businessType: string;
  operatingDays: string[];
  deliveryAvailable: boolean;
  packagingCharges: number;
  minimumOrderValue: number;
  parkingAvailable: boolean;
  alcoholServed: boolean;
  liveMusic: boolean;
  acceptsTableBookings: boolean;
  geoLocation: GeoLocation;
  location: RestaurantLocation;
  avgPreparationTime: number;
  operatingHours: {
    openTime: string;
    closeTime: string;
  };
  deliveryRadius: number;
  acAvailable: string;
  restaurantCategory: string;
  seatingCapacity: number;
  kitchenPhotos: string[];
  foodItemImages: string[];
}

interface OrderItem {
  _id: string;
  orderId: string;
  userId: string;
  restaurantId: string;
  dishId: string;
  variantId: string | null;
  addons: any[];
  dishName: string;
  variantName: string | null;
  addonNames: string[];
  price: number;
  addonPrice: number;
  totalItemPrice: number;
  quantity: number;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface Order {
  _id: string;
  userId: string;
  restaurantId: string;
  subtotal: number;
  taxAmount: number;
  deliveryFee: number;
  totalAmount: number;
  paymentMethod: string;
  paymentStatus: string;
  orderStatus: string;
  deliveryAddress: DeliveryAddress;
  cookingInstructions?: string | null;
  deliveryInstructions?: string | null;
  createdAt: string;
  updatedAt: string;
  __v: number;
  restaurant: Restaurant;
  items: OrderItem[];
  totalItems: number;
}

export interface OrdersResponse {
  status: boolean;
  data: Order[];
  pagination: {
    total: number;
    currentPage: number;
    totalPages: number;
  };
  message: string;
}

interface CartData {
  dishId: string;
  quantity: number;
  restaurantId: string;
  variantId?: string | null;
  addons?: any[];
}

interface PaymentData {
  orderId: string;
  amount: number;
  method: string;
}

interface PaymentMethod {
  type: string;
  details: any;
}

interface DeliveryData {
  restaurantId: string;
  deliveryAddress: string;
}

interface ReviewData {
  restaurantId: string;
  rating: number;
  comment: string;
}

interface CategoryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  isActive?: boolean;
}

// User/Auth API calls
export const authService = {
  sendOtp: (phoneNumber: string) =>
    apiClient.post('/user/send-otp', { phoneNumber }),

  verifyOtp: (phoneNumber: string, otp: string) =>
    apiClient.post('/user/verify-otp', { phoneNumber, otp }),

  login: (email: string, password: string) =>
    apiClient.post('/auth/login', { email, password }),

  register: (userData: UserData) =>
    apiClient.post('/auth/register', userData),

  logout: () =>
    apiClient.post('/auth/logout'),

  getCurrentUser: () =>
    apiClient.get('/auth/me'),

  getUserProfile: () =>
    apiClient.get('/user/profile'),

  updateProfile: (userData: UpdateProfileData) =>
    apiClient.put('/user/profile', userData),
};

// Restaurant API calls
export const restaurantService = {
  getAllRestaurants: (params?: RestaurantParams) =>
    apiClient.get('/restaurants', { params }),

  getSingleRestaurant: (id: string) =>
    apiClient.get(`/user/restaurant/${id}`),

  searchRestaurants: (query: string) =>
    apiClient.get('/restaurants/search', { params: { q: query } }),

  getRestaurantsByCategory: (id: string) =>
    apiClient.get(`user/dish/restaurant/${id}/group-counts?groupBy=category`),

  searchDishes: (params: { categoryId?: string; page?: number; limit?: number; restaurantId: string; searchText?: string }) =>
    apiClient.get('/user/dish/search', { params }),

  getRatings: (restaurantId: string) =>
    apiClient.get(`/restaurants/${restaurantId}/ratings`),

  getUserRestaurants: (params?: RestaurantParams) =>
    apiClient.get('/user/restaurant', { params }),

  getRestaurantsByGroup: (groupBy: string, groupbyId: string, params?: { limit?: number; page?: number }) =>
    apiClient.get(`/user/restaurant/by-group/${groupBy}/${groupbyId}`, { params }),
};

// Menu/Food Items API calls
export const foodService = {
  getMenuItems: (restaurantId: string) =>
    apiClient.get(`/restaurants/${restaurantId}/menu`),

  getFoodItemById: (id: string) =>
    apiClient.get(`/food/${id}`),

  searchFood: (query: string) =>
    apiClient.get('/food/search', { params: { q: query } }),

  getFoodByCategory: (restaurantId: string, category: string) =>
    apiClient.get(`/restaurants/${restaurantId}/menu/${category}`),
};

interface PricingData {
  restaurantId: string;
  items: Array<{
    dishId: string;
    variantId: string | null;
    addons: any[];
    quantity: number;
  }>;
  paymentMethod: string;
  couponCode?: string;
  deliveryAddress?: {
    fullName: string;
    phone: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    pincode: string;
  };
}

export interface PricingResponse {
  status: boolean;
  data: {
    items: Array<{
      dishId: string;
      variantId: string | null;
      addons: any[];
      dishName: string;
      addonNames: string[];
      price: number;
      addonPrice: number;
      totalItemPrice: number;
      quantity: number;
    }>;
    subtotal: number;
    taxAmount: number;
    deliveryFee: number;
    totalAmount: number;
    couponDiscount?: number;
    minimumOrderValue: number;
    meetsMinimumOrder: boolean;
  };
  message: string;
}

// Order API calls (aligned with mobile App/Services/Order.ts)
export const orderService = {
  createOrder: (orderData: OrderData) =>
    apiClient.post<{ status: boolean; data: CreateOrderResponse; message: string }>('/user/order', orderData),

  getOrders: (params?: OrderParams) =>
    apiClient.get('/user/order', { params }),

  getOrderById: (id: string) =>
    apiClient.get(`/user/order/${id}`),

  getPricing: (pricingData: PricingData) =>
    apiClient.post<PricingResponse>('/user/order/pricing', pricingData),

  getApplicableCoupons: (restaurantId: string, orderValue: number) =>
    apiClient.get<{ status: boolean; data: CouponData[]; message: string }>(
      `/user/coupons/applicable?restaurantId=${restaurantId}&orderValue=${orderValue}`
    ),

  validateCoupon: (data: {
    couponCode: string;
    restaurantId: string;
    orderValue: number;
  }) =>
    apiClient.post<{
      status: boolean;
      data: {
        isValid?: boolean;
        discountAmount?: number;
        finalAmount?: number;
        coupon?: CouponData;
        message?: string;
      } & CouponData;
      message: string;
    }>('/user/coupons/validate', data),

  trackOrder: (orderId: string) =>
    apiClient.get(`/user/order/${orderId}/track`),

  rateRestaurant: (data: {
    orderId: string;
    restaurantId: string;
    rating: number;
    review: string;
  }) => apiClient.post('/user/rating/restaurant', data),

  rateDeliveryAgent: (data: {
    orderId: string;
    deliveryAgentId: string;
    rating: number;
    review: string;
  }) => apiClient.post('/user/rating/delivery-agent', data),

  downloadInvoice: async (orderId: string): Promise<Blob> => {
    const token =
      localStorage.getItem('authToken') ?? localStorage.getItem('token') ?? '';
    const baseURL = process.env.REACT_APP_API_URL;
    const response = await fetch(`${baseURL}/user/order/${orderId}/invoice`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        userType: 'User',
        Accept: 'application/pdf,application/octet-stream,*/*',
      },
    });

    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const isPdf =
      bytes.length >= 4 &&
      bytes[0] === 0x25 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x44 &&
      bytes[3] === 0x46;

    if (isPdf) {
      return new Blob([buffer], { type: 'application/pdf' });
    }

    const text = new TextDecoder().decode(bytes);
    let json: any = null;
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }

    const remoteUrl =
      json?.data?.url || json?.data?.link || json?.url || json?.link;
    if (typeof remoteUrl === 'string' && remoteUrl) {
      const fileRes = await fetch(remoteUrl);
      const fileBuf = await fileRes.arrayBuffer();
      return new Blob([fileBuf], { type: 'application/pdf' });
    }

    if (!response.ok) {
      throw new Error(json?.message || text || 'Failed to download invoice');
    }
    throw new Error(json?.message || 'Invoice is not available for this order');
  },

  updateOrderInstructions: async (
    orderId: string,
    data: { deliveryInstructions?: string; cookingInstructions?: string }
  ) => {
    try {
      return await apiClient.patch(`/user/order/${orderId}`, data);
    } catch {
      return apiClient.put(`/user/order/${orderId}`, data);
    }
  },

  // Group order APIs (aligned with mobile OrderService)
  createGroupOrder: (data: {
    restaurantId: string;
    maxMembers: number;
    deliveryAddress: {
      fullName: string;
      phone: string;
      addressLine1: string;
      addressLine2?: string;
      city: string;
      pincode: string;
      geoLocation?: GeoLocation;
    };
    name: string;
  }) => apiClient.post('/user/group-order', data),

  getGroupOrderSession: (sessionId: string) =>
    apiClient.get(`/user/group-order/${sessionId}`),

  joinGroupOrder: (inviteToken: string) =>
    apiClient.post(`/user/group-order/join/${inviteToken}`, {}),

  getOwnGroupSessions: () =>
    apiClient.get('/user/group-order'),

  addGroupOrderItem: (sessionId: string, data: { dishId: string; quantity: number; variantId?: string | null; addons?: any[] }) =>
    apiClient.post(`/user/group-order/${sessionId}/item`, data),

  updateGroupOrderItem: (sessionId: string, itemId: string, data: { quantity: number }) =>
    apiClient.put(`/user/group-order/${sessionId}/item/${itemId}`, data),

  deleteGroupOrderItem: (sessionId: string, itemId: string) =>
    apiClient.delete(`/user/group-order/${sessionId}/item/${itemId}`),
};

// Cart API calls (aligned with mobile App/Services/Cart.ts)
export const cartService = {
  addToCart: (cartData: CartData) =>
    apiClient.post('/user/cart', cartData),

  getCart: () =>
    apiClient.get('/user/cart'),

  updateCartItem: (cartItemId: string, quantity: number) =>
    apiClient.patch(`/user/cart/${cartItemId}`, { quantity }),

  removeCartItem: (cartItemId: string) =>
    apiClient.delete(`/user/cart/item/${cartItemId}`),

  clearCart: () =>
    apiClient.delete('/user/cart'),
};

// Payment API calls
export const paymentService = {
  initiatePayment: (paymentData: PaymentData) =>
    apiClient.post('/payments/initiate', paymentData),

  verifyPayment: (paymentId: string, transactionId: string) =>
    apiClient.post(`/payments/${paymentId}/verify`, { transactionId }),

  getPaymentMethods: () =>
    apiClient.get('/payments/methods'),

  savePaymentMethod: (methodData: PaymentMethod) =>
    apiClient.post('/payments/methods', methodData),

  deletePaymentMethod: (methodId: string) =>
    apiClient.delete(`/payments/methods/${methodId}`),
};

// Delivery API calls
export const deliveryService = {
  getDeliveryEstimate: (deliveryData: DeliveryData) =>
    apiClient.post('/delivery/estimate', deliveryData),

  getDeliveryStatus: (orderId: string) =>
    apiClient.get(`/delivery/${orderId}/status`),

  updateDeliveryAddress: (orderId: string, address: string) =>
    apiClient.put(`/delivery/${orderId}/address`, { address }),
};

// Review API calls
export const reviewService = {
  addReview: (reviewData: ReviewData) =>
    apiClient.post('/reviews', reviewData),

  getReviews: (restaurantId: string) =>
    apiClient.get(`/restaurants/${restaurantId}/reviews`),

  deleteReview: (reviewId: string) =>
    apiClient.delete(`/reviews/${reviewId}`),
};

// Favorites API calls
export const favoriteService = {
  addFavorite: (restaurantId: string) =>
    apiClient.post(`/favorites/${restaurantId}`),

  removeFavorite: (restaurantId: string) =>
    apiClient.delete(`/favorites/${restaurantId}`),

  getFavorites: () =>
    apiClient.get('/favorites'),

  isFavorite: (restaurantId: string) =>
    apiClient.get(`/favorites/${restaurantId}/check`),
};

// Category API calls
export const categoryService = {
  getCategories: (params?: CategoryParams) =>
    apiClient.get('/user/category', { params }),

  getCategoryById: (id: string) =>
    apiClient.get(`/user/category/${id}`),
};

// Address Book Types
export interface AddressBookData {
  _id?: string;
  userId?: string;
  fullName: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  isDefault: boolean;
  geoLocation?: {
    type: string;
    coordinates: [number, number]; // [longitude, latitude]
  };
  deliveryInstructions?: string | string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateAddressData {
  fullName: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  isDefault?: boolean;
  geoLocation?: {
    type: string;
    coordinates: [number, number]; // [longitude, latitude]
  };
  deliveryInstructions?: string;
}

export interface UpdateAddressData {
  fullName?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  isDefault?: boolean;
  geoLocation?: {
    type: string;
    coordinates: [number, number]; // [longitude, latitude]
  };
  deliveryInstructions?: string;
}

// Notification Types
export interface NotificationItem {
  _id: string;
  recipientId: string;
  recipientType: string;
  event: string;
  title: string;
  message: string;
  priority: string;
  category: string;
  data: {
    event?: string;
    orderId?: string;
    restaurantId?: string;
    deliveryAgentId?: string;
    status?: string;
    [key: string]: unknown;
  };
  deepLink: string;
  groupKey: string;
  readAt: string | null;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationsResponse {
  status: boolean;
  message: string;
  data: NotificationItem[];
  pagination: {
    total: number;
    currentPage: number;
    totalPages: number;
    unreadCount: number;
  };
}

export interface UnreadCountResponse {
  status: boolean;
  message: string;
  data: {
    unreadCount: number;
  };
}

// Notification API calls (aligned with mobile App/Services/Notification.ts)
export const notificationService = {
  getNotifications: (params?: { page?: number; limit?: number }) =>
    apiClient.get('/user/notifications', { params }),

  getUnreadCount: () =>
    apiClient.get('/user/notifications/unread-count'),

  markAsRead: (notificationId: string) =>
    apiClient.patch(`/user/notifications/${notificationId}/read`, {}),

  markAllAsRead: () =>
    apiClient.patch('/user/notifications/read-all', {}),
};

// Home / banners (aligned with mobile App/Services/Home .ts)
export const homeService = {
  getBanners: () =>
    apiClient.get<{ status: boolean; data: BannerData[]; message: string }>('/user/banner'),

  search: (keyword: string, dishLimit = 10, restaurantLimit = 10) =>
    apiClient.get('/user/search', {
      params: { keyword, dishLimit, restaurantLimit },
    }),
};

// Static content (aligned with mobile App/Services/StaticContent.ts)
export const staticContentService = {
  getStaticPage: (slug: StaticPageSlug) =>
    apiClient.get<{ status: boolean; data: StaticPageData; message: string }>(
      `/user/static-content/${slug}`
    ),
};

// Subscription / Duddus+ (aligned with typical mobile membership APIs)
export const subscriptionService = {
  getPlans: () =>
    apiClient.get<{ status: boolean; data: any[]; message: string }>('/user/subscription/plans'),

  subscribe: (data: { planId?: string; durationMonths: number; amount: number }) =>
    apiClient.post<{ status: boolean; data: any; message: string }>('/user/subscription', data),
};

// App feedback / Rate Us
export const feedbackService = {
  submitAppRating: (data: { rating: number; feedback?: string }) =>
    apiClient.post<{ status: boolean; message: string }>('/user/feedback', data),
};

// Profile image upload (aligned with mobile AuthService.uploadImage)
const parseUploadedImageLink = (payload: any): string => {
  if (!payload) return '';
  if (typeof payload === 'string' && payload.startsWith('http')) return payload;
  const candidates = [
    payload?.data?.link,
    payload?.data?.url,
    payload?.data?.image,
    payload?.data?.file,
    payload?.data?.path,
    payload?.link,
    payload?.url,
    payload?.image,
  ];
  const match = candidates.find(
    (value) => typeof value === 'string' && value && !value.startsWith('blob:')
  );
  return match || '';
};

export const uploadService = {
  uploadProfileImage: async (file: File): Promise<{ status: boolean; data: { link: string }; message: string }> => {
    const token =
      localStorage.getItem('authToken') ?? localStorage.getItem('token') ?? '';
    const baseURL = process.env.REACT_APP_API_URL;
    const formData = new FormData();
    formData.append('file', file);

    // Mobile uses POST {MAIN_BASE_URL}/upload/profile (Postman: /upload/:location)
    const response = await fetch(`${baseURL}/upload/profile`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        userType: 'User',
      },
      body: formData,
    });

    let json: any = null;
    try {
      json = await response.json();
    } catch {
      json = null;
    }

    const link = parseUploadedImageLink(json);
    if (link) {
      return {
        status: true,
        data: { link },
        message: json?.message || 'Uploaded',
      };
    }

    if (!response.ok || json?.status === false) {
      try {
        const fallback: any = await apiClient.post('/upload/profile', formData);
        const fallbackLink = parseUploadedImageLink(fallback);
        if (fallbackLink) {
          return {
            status: true,
            data: { link: fallbackLink },
            message: fallback?.message || 'Uploaded',
          };
        }
        return fallback;
      } catch {
        throw new Error(json?.message || 'Failed to upload profile image');
      }
    }

    return json;
  },

  uploadImage: async (
    file: File,
    location: string
  ): Promise<{ status: boolean; data: { link: string }; message: string }> => {
    const formData = new FormData();
    formData.append("file", file);

    return apiClient.post(`/upload/${location}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }) as Promise<{ status: boolean; data: { link: string }; message: string }>;
  },

  uploadMultipleImages: async (files: File[], location: string) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    return apiClient.post(`/upload/${location}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
};

// Support chat (aligned with mobile App/Services/Support.ts)
export const supportService = {
  getThreads: (page = 1, limit = 20) =>
    apiClient.get('/user/support/thread', { params: { page, limit } }),

  getMessages: (page = 1, limit = 20) =>
    apiClient.get('/user/support/messages', { params: { page, limit } }),

  sendMessage: (data: { body: string }) =>
    apiClient.post('/user/support/messages', data),

  markAsRead: () => apiClient.patch('/user/support/read', {}),
};

// Address Book API calls
export const addressBookService = {
  // Get all addresses for the user
  getAllAddresses: () =>
    apiClient.get<{ status: boolean; data: AddressBookData[]; message: string }>('/user/addressbook'),

  // Create a new address
  createAddress: (addressData: CreateAddressData) =>
    apiClient.post<{ status: boolean; data: AddressBookData; message: string }>('/user/addressbook', addressData),

  // Update an existing address
  updateAddress: (addressId: string, addressData: UpdateAddressData) =>
    apiClient.put<{ status: boolean; data: AddressBookData; message: string }>(`/user/addressbook/${addressId}`, addressData),

  // Set an address as default
  setDefaultAddress: (addressId: string) =>
    apiClient.patch<{ status: boolean; data: AddressBookData; message: string }>(`/user/addressbook/${addressId}/default`),

  // Delete an address
  deleteAddress: (addressId: string) =>
    apiClient.delete<{ status: boolean; message: string }>(`/user/addressbook/${addressId}`),
};

